import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { CareLogForm } from './-care-log-form'
import type { CareLogActivityType } from '@/lib/api/care-logs-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { GrowerSearchSelect } from '@/components/growers/grower-search-select'
import { useAuth } from '@/hooks/use-auth'
import {
    useCareLog,
    useCareLogs,
    useCreateCareLog,
    useDeleteCareLog,
    useUpdateCareLog,
} from '@/hooks/use-care-logs-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/care-logs',
)({
    component: CareLogsPage,
})

const ALL = 'all'

const activityLabel: Record<CareLogActivityType, () => string> = {
    fertilizing: m.care_log_activity_fertilizing,
    harvesting: m.care_log_activity_harvesting,
    replanting: m.care_log_activity_replanting,
    spraying: m.care_log_activity_spraying,
    weeding: m.care_log_activity_weeding,
    watering: m.care_log_activity_watering,
    pruning: m.care_log_activity_pruning,
    bud_picking: m.care_log_activity_bud_picking,
    other: m.care_log_activity_other,
}

function CareLogsPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('care_logs.write')
    const canDelete = hasPermission('care_logs.delete')

    const [growerFilter, setGrowerFilter] = useState('')
    const [activityFilter, setActivityFilter] = useState(ALL)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [creating, setCreating] = useState(false)

    const { data } = useCareLogs({
        per_page: 100,
        grower_id: growerFilter || undefined,
        activity_type: activityFilter === ALL ? undefined : activityFilter,
    })
    const rows = data?.data ?? []

    const { data: editing } = useCareLog(editingId)

    const createMut = useCreateCareLog()
    const updateMut = useUpdateCareLog(editingId ?? '')
    const deleteMut = useDeleteCareLog()

    const dialogOpen = creating || (!!editingId && !!editing)

    const closeDialog = () => {
        setCreating(false)
        setEditingId(null)
    }

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">{m.care_log_title()}</h1>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditingId(null)
                            setCreating(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.care_log_new()}
                    </Button>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                <GrowerSearchSelect
                    value={growerFilter || undefined}
                    onChange={setGrowerFilter}
                    placeholder={m.care_log_filter_all_growers()}
                    allowClear
                    className="w-56"
                />
                <Select
                    value={activityFilter}
                    onValueChange={setActivityFilter}
                >
                    <SelectTrigger className="w-56">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>
                            {m.care_log_filter_all_activities()}
                        </SelectItem>
                        {(
                            Object.keys(activityLabel) as CareLogActivityType[]
                        ).map((at) => (
                            <SelectItem key={at} value={at}>
                                {activityLabel[at]()}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.care_log_log_date()}</TableHead>
                        <TableHead>{m.care_log_grower()}</TableHead>
                        <TableHead>{m.care_log_activity_type()}</TableHead>
                        <TableHead>{m.care_log_quantity()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>{row.log_date.slice(0, 10)}</TableCell>
                            <TableCell className="font-medium">
                                {row.grower_name || '—'}
                            </TableCell>
                            <TableCell>
                                {activityLabel[row.activity_type]?.() ??
                                    row.activity_type}
                            </TableCell>
                            <TableCell>{row.quantity}</TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                            setCreating(false)
                                            setEditingId(row.id)
                                        }}
                                    >
                                        <Pencil className="size-4" />
                                    </Button>
                                )}
                                {canDelete && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    m.care_log_delete_confirm(),
                                                )
                                            ) {
                                                deleteMut.mutate(row.id)
                                            }
                                        }}
                                    >
                                        <Trash2 className="size-4" />
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog
                open={dialogOpen}
                onOpenChange={(o) => {
                    if (!o) closeDialog()
                }}
            >
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingId ? m.care_log_edit() : m.care_log_new()}
                        </DialogTitle>
                    </DialogHeader>
                    {dialogOpen && (
                        <CareLogForm
                            key={editingId ?? 'new'}
                            initial={editingId ? (editing ?? null) : null}
                            onSubmit={async (values) => {
                                try {
                                    if (editingId) {
                                        await updateMut.mutateAsync(values)
                                    } else {
                                        await createMut.mutateAsync(values)
                                    }
                                    closeDialog()
                                } catch {
                                    // mutation onError surfaces the toast
                                }
                            }}
                            submitting={
                                createMut.isPending || updateMut.isPending
                            }
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}
